import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ChevronDown, ChevronLeft, ChevronRight,
  Monitor, AlertTriangle, Clock, Eye, ShieldAlert, BarChart3,
  DollarSign, TrendingUp, Target, SlidersHorizontal, Layers,
  HelpCircle, Gauge, Wallet, CalendarClock, MousePointerClick
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';



const WHATSAPP_URL = 'https://wa.me/5588996527079';

const problems = [
  {
    icon: AlertTriangle,
    title: 'Estouro de Orçamento',
    description: 'Contas gastam além do previsto sem que ninguém perceba a tempo. O prejuízo só aparece no extrato.'
  },
  {
    icon: ShieldAlert,
    title: 'Contas Paradas por Erro de Pagamento',
    description: 'Cartões expirados ou saldo insuficiente param campanhas inteiras. Você só descobre quando o cliente reclama.'
  },
  {
    icon: Clock,
    title: 'Perda de Tempo Verificando Conta por Conta',
    description: 'Abrir cada Gerenciador de Anúncios individualmente todos os dias consome horas que poderiam ser estratégicas.'
  }
];

const solutions = [
  {
    icon: Layers,
    title: 'Visão Consolidada',
    description: 'Acesse todas as suas contas Meta e Google Ads em um único painel, sem precisar trocar de login.'
  },
  {
    icon: AlertTriangle,
    title: 'Alertas Inteligentes',
    description: 'Identifique instantaneamente contas com problemas de saldo ou performance através de alertas visuais.'
  },
  {
    icon: BarChart3,
    title: 'KPIs Estratégicos',
    description: 'Visualize métricas agregadas ou individuais por cliente para tomadas de decisão rápidas e precisas.'
  }
];

const differentiators = [
  {
    icon: Target,
    title: 'Limites de Gasto',
    description: 'Configure limites de orçamento personalizados para cada cliente e receba avisos antes de qualquer estouro.'
  },
  {
    icon: Gauge,
    title: 'Pacing Inteligente',
    description: 'Acompanhe o ritmo de gasto diário ajustado automaticamente ao modelo de negócio de cada operação.'
  },
  {
    icon: BarChart3,
    title: 'Métricas sob Medida',
    description: 'Escolha quais KPIs são críticos para cada conta, garantindo que o painel mostre apenas o que importa.'
  }
];

const metrics = [
  { icon: Wallet, label: 'Saldo Atual e Restante', desc: 'Quanto cada conta ainda tem disponível para investir no mês' },
  { icon: Gauge, label: '% Eficiência do Investimento', desc: 'Ritmo de investimento vs. orçamento planejado, além de sugestão pros dias restantes do mês' },
  { icon: AlertTriangle, label: 'Alertas Personalizados', desc: 'Critérios definidos por você que ajudam a mostrar quais contas precisam de mais atenção' },
  { icon: DollarSign, label: 'Total Investido', desc: 'Diireto do Gerenciador, para cada conta' },
  { icon: MousePointerClick, label: 'Cliques', desc: 'Volume de cliques por conta ou consolidado' },
  { icon: Target, label: 'Resultados', desc: 'Mensagens, Leads, Vendas, etc.' }
  
];

const faqs = [
  {
    question: 'Quantas contas de anúncios consigo monitorar?',
    answer: 'Não há um limite fixo. Todas as suas contas podem ser conectadas e monitoradas no dashboard. Porém, para conectar todas do Meta Ads é preciso de conector externo e pago, como o Stract.to'
  },
  {
    question: 'Funciona com Meta Ads e Google Ads ao mesmo tempo?',
    answer: 'Sim. Os dados de ambas as plataformas são integrados no mesmo dash, permitindo visão cruzada e consolidada.'
  },
  {
    question: 'Os alertas visuais de risco são automáticos?',
    answer: 'Sim. Eles são configurados com base em regras que você define (ex.: saldo abaixo de X, gasto acima de Y). O dashboard destaca visualmente as contas que precisam de atenção.'
  },
  {
    question: 'Posso ter tanto o controle de Saldos como o de Orçamentos?',
    answer: 'Sim. Vale destacar que para esses 2 controles é necessário uma planilha auxiliar também que disponibilizo. Para os KPIs das contas já é todo automático, com os dados do gerenciador. '
  },
  {
    question: 'Quanto tempo leva para configurar?',
    answer: 'Depende do nível de personalização e sua disponibilidade para testar e validar tudo, podendo levar de alguns dias até uma semana.'
  }
];

const clientLogos = [
  'Keep Ads', 'Academia das Redes Sociais', 'Agência Propulse', 'Agência Skeet', 'Agência Wood'
];

const showcaseImages = [
  '/assets/SaldosGoogle.jpeg',
  '/assets/OrcMeta.jpeg',
  '/assets/SaldosOrcGoogleMeta.jpeg',
  '/assets/metricasGoogleMeta.jpeg'
];

const MonitoramentoMultiContasLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const nextImage = () => setCurrentImage((p) => (p + 1) % showcaseImages.length);
  const prevImage = () => setCurrentImage((p) => (p - 1 + showcaseImages.length) % showcaseImages.length);

  return (
    <>
      <Helmet>
        <title>Monitoramento Multi-Contas Ads | BI para Agências</title>
        <meta name="description" content="Centralize a gestão de dezenas de contas de anúncios em um único painel. Alertas de orçamento e performance para grandes operações." />
      </Helmet>
    <div className="page-mc-landing">

      {/* ===== HERO MULTI-CONTAS ===== */}
<section className="mc-hero" data-testid="mc-hero" style={{ overflow: 'hidden', padding: '40px 0' }}>
  <div className="rl-container"> {/* Usando rl-container para manter a largura total */}
    <Link to="/services" className="back-link" data-testid="mc-back-link" style={{ marginBottom: '20px', display: 'inline-flex' }}>
      <ArrowLeft size={18} />
      Voltar aos Serviços
    </Link>

    <div className="bv-hero-grid" style={{ 
      display: 'grid', 
      gridTemplateColumns: '1.3fr 0.7fr', 
      gap: '60px', 
      alignItems: 'center', 
      marginTop: '40px' 
    }}>
      <div className="mc-hero-content">
        <div className="mc-hero-badge">
          <Monitor size={14} />
          Multi-Contas Ads
        </div>
        <h1 className="mc-hero-title" style={{ marginTop: '15px', fontSize: '2.4rem', lineHeight: '1.2' }}>
          Gerencie dezenas de contas de anúncio <span className="mc-accent">em uma única tela</span>
        </h1>
        <p className="mc-hero-subtitle" style={{ marginTop: '20px', marginBottom: '30px', fontSize: '1.1rem' }}>
          Monitore orçamentos, saldos e KPIs de todas as suas contas do Meta e Google Ads
          sem precisar abrir um gerenciador por vez.
        </p>
        <div className="mc-hero-cta-group">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-large" data-testid="mc-hero-cta">
            Quero Centralizar Minhas Contas
            <ArrowRight size={20} />
          </a>
        </div>
      </div>

      <div className="mc-hero-visual hide-on-mobile" aria-hidden="true" style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ 
          width: '100%', 
          maxWidth: '380px', 
          backgroundColor: '#161616', 
          padding: '20px', 
          borderRadius: '16px', 
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {[
            { name: 'Cliente A: Meta', status: 'OK', color: '#34d399', icon: <Eye size={14}/> },
            { name: 'Cliente B: Google', status: 'Atenção', color: '#fbbf24', icon: <AlertTriangle size={14}/> },
            { name: 'Cliente C: Meta', status: 'OK', color: '#34d399', icon: <Eye size={14}/> },
            { name: 'Cliente D: Google', status: 'Crítico', color: '#f87171', icon: <ShieldAlert size={14}/> },
            { name: 'Cliente E: Meta', status: 'Atenção', color: '#fbbf24', icon: <AlertTriangle size={14}/> }
          ].map((acc, i) => (
            <div key={i} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              padding: '12px 16px', 
              backgroundColor: 'rgba(255,255,255,0.03)', 
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: acc.color }}>{acc.icon}</span>
                <span style={{ fontSize: '13px', fontWeight: '500' }}>{acc.name}</span>
              </div>
              <span style={{ fontSize: '10px', fontWeight: 'bold', color: acc.color, textTransform: 'uppercase' }}>{acc.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
</section>

      {/* ===== PROBLEMA ===== */}
      <section className="mc-problems" data-testid="mc-problems">
        <div className="mc-container">
          <div className="mc-section-header">
            <h2 className="mc-section-title">O Risco de Não Ter Controle Centralizado</h2>
            <p className="mc-section-subtitle">
              Problemas que podem custar dinheiro e credibilidade para sua agência
            </p>
          </div>
          <div className="mc-problems-grid">
            {problems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="mc-problem-card" data-testid={`mc-problem-${i}`}>
                  <div className="mc-problem-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="mc-problem-title">{item.title}</h3>
                  <p className="mc-problem-text">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== SOLUÇÃO (CARTÕES) ===== */}
<section className="mc-solution" data-testid="mc-solution" style={{ padding: '80px 0', backgroundColor: '#0a0a0a' }}>
  <div className="rl-container"> {/* Mantendo o container largo padronizado */}
    <div className="mc-section-header" style={{ marginBottom: '50px', textAlign: 'center' }}>
      <h2 className="mc-section-title">Um Painel Unificado com Alertas de Risco</h2>
      <p className="mc-section-subtitle" style={{ maxWidth: '700px', margin: '15px auto 0', opacity: 0.8 }}>
        Dashboard personalizável que consolida todas as contas de anúncios em uma única visão, 
        destacando automaticamente o que precisa de atenção.
      </p>
    </div>

    {/* GRID DE SOLUÇÕES */}
    <div className="mc-problems-grid">
      {solutions.map((item, i) => {
        const Icon = item.icon;
        return (
          <div key={i} className="mc-problem-card" style={{ 
            backgroundColor: '#161616', 
            padding: '35px', 
            borderRadius: '16px', 
            border: '1px solid rgba(255,255,255,0.05)',
            transition: 'transform 0.3s ease'
          }}>
            <div className="mc-problem-icon" style={{ 
              color: '#34d399', // Verde para indicar solução/sucesso
              marginBottom: '20px',
              display: 'inline-flex',
              padding: '12px',
              backgroundColor: 'rgba(52, 211, 153, 0.1)',
              borderRadius: '12px'
            }}>
              <Icon size={28} />
            </div>
            <h3 className="mc-problem-title" style={{ fontSize: '1.25rem', marginBottom: '12px', color: '#fff' }}>
              {item.title}
            </h3>
            <p className="mc-problem-text" style={{ fontSize: '0.95rem', lineHeight: '1.6', opacity: 0.7 }}>
              {item.description}
            </p>
          </div>
        );
      })}
    </div>
  </div>
</section>

      {/* ===== MÉTRICAS ===== */}
      <section className="mc-metrics" data-testid="mc-metrics">
        <div className="mc-container">
          <div className="mc-section-header">
            <h2 className="mc-section-title">Métricas de Controle</h2>
            <p className="mc-section-subtitle">
              Tudo o que você precisa ver de cada conta, consolidado ou individual
            </p>
          </div>
          <div className="mc-metrics-grid">
            {metrics.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="mc-metric-card" data-testid={`mc-metric-${i}`}>
                  <div className="mc-metric-icon">
                    <Icon size={24} />
                  </div>
                  <span className="mc-metric-label">{item.label}</span>
                  <span className="mc-metric-desc">{item.desc}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== DIFERENCIAIS (CARTÕES) ===== */}
<section className="mc-differentiator" data-testid="mc-differentiator" style={{ padding: '80px 0', backgroundColor: '#0f0f0f' }}>
  <div className="rl-container">
    <div className="mc-section-header" style={{ marginBottom: '50px', textAlign: 'center' }}>
      <h2 className="mc-section-title">Personalização Total dos Critérios</h2>
      <p className="mc-section-subtitle" style={{ maxWidth: '750px', margin: '15px auto 0', opacity: 0.8 }}>
        Cada cliente tem uma realidade única. Nossa solução permite configurar critérios de risco 
        e limites de alerta de forma totalmente independente por operação.
      </p>
    </div>

    {/* GRID DE DIFERENCIAIS */}
    <div className="mc-problems-grid">
      {differentiators.map((item, i) => {
        const Icon = item.icon;
        return (
          <div key={i} className="mc-problem-card" style={{ 
            backgroundColor: '#1a1a1a', 
            padding: '30px', 
            borderRadius: '16px', 
            border: '1px solid rgba(251, 191, 36, 0.1)', // Borda sutil amarela para diferenciar
            textAlign: 'center'
          }}>
            <div className="mc-problem-icon" style={{ 
              color: '#fbbf24', 
              marginBottom: '20px',
              display: 'inline-flex',
              padding: '12px',
              backgroundColor: 'rgba(251, 191, 36, 0.1)',
              borderRadius: '50%' // Ícone arredondado para variar o design
            }}>
              <Icon size={24} />
            </div>
            <h3 style={{ fontSize: '1.2rem', marginBottom: '12px', color: '#fff', fontWeight: '600' }}>
              {item.title}
            </h3>
            <p style={{ fontSize: '0.9rem', lineHeight: '1.6', opacity: 0.7, margin: 0 }}>
              {item.description}
            </p>
          </div>
        );
      })}
    </div>
  </div>
</section>

      {/* ===== PROVA SOCIAL ===== */}
      <section className="mc-social-proof" data-testid="mc-social-proof">
        <div className="mc-container">
          <div className="mc-section-header">
            <h2 className="mc-section-title">Agências que Já Utilizam a Solução</h2>
          </div>
        </div>
        <div className="mc-logos-track-wrapper">
          <div className="mc-logos-track">
            {[...clientLogos, ...clientLogos].map((name, i) => (
              <div key={i} className="mc-logo-item" data-testid={`mc-logo-${i}`}>
                <span>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CARROSSEL ===== */}
      <section className="mc-showcase" data-testid="mc-showcase">
        <div className="mc-container">
          <div className="mc-section-header">
            <h2 className="mc-section-title">Veja os dashboards em Ação</h2>
            <p className="mc-section-subtitle">Modelos dos dashboards para monitoramento multi-contas</p>
          </div>
          <div className="portfolio-detail-container">
            <div className="portfolio-image-carousel">
              <img src={showcaseImages[currentImage]} alt={`Painel exemplo ${currentImage + 1}`} className="portfolio-carousel-image" />
              {showcaseImages.length > 1 && (
                <>
                  <button className="carousel-btn carousel-btn-prev" onClick={prevImage} aria-label="Imagem anterior" data-testid="mc-carousel-prev"><ChevronLeft size={24} /></button>
                  <button className="carousel-btn carousel-btn-next" onClick={nextImage} aria-label="Próxima imagem" data-testid="mc-carousel-next"><ChevronRight size={24} /></button>
                  <div className="carousel-indicators">
                    {showcaseImages.map((_, index) => (
                      <button key={index} className={`carousel-indicator ${index === currentImage ? 'active' : ''}`} onClick={() => setCurrentImage(index)} aria-label={`Ir para imagem ${index + 1}`} />
                    ))}
                  </div>
                  <div className="carousel-counter">{currentImage + 1} / {showcaseImages.length}</div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="mc-faq" data-testid="mc-faq">
        <div className="mc-container mc-faq-container">
          <div className="mc-section-header">
            <div className="faq-icon-wrapper">
              <HelpCircle size={32} />
            </div>
            <h2 className="mc-section-title">Perguntas Frequentes</h2>
          </div>
          <div className="faq-grid-modern">
            {faqs.map((faq, index) => (
              <div key={index} className={`faq-card-modern ${openFaq === index ? 'faq-card-open' : ''}`}>
                <button className="faq-question-modern" onClick={() => setOpenFaq(openFaq === index ? null : index)} data-testid={`mc-faq-question-${index}`}>
                  <span className="faq-number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="faq-question-text">{faq.question}</span>
                  <ChevronDown size={20} className={`faq-chevron ${openFaq === index ? 'faq-chevron-open' : ''}`} />
                </button>
                <div className={`faq-answer-modern ${openFaq === index ? 'faq-answer-visible' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="mc-final-cta" data-testid="mc-final-cta">
        <div className="mc-container">
          <div className="mc-final-cta-content">
            <h2 className="mc-final-cta-title">Vamos Estruturar o Seu Dash de Controle Geral?</h2>
            <p className="mc-final-cta-text">
              Me conte quantas contas você gerencia e quais métricas são mais críticas para a saúde delas. Eu monto tudo sob medida e 100% personalizado.
            </p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-primary btn-large" data-testid="mc-final-cta-btn">
              Quero Centralizar Minhas Contas
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default MonitoramentoMultiContasLanding;
